import type { ProductDescriptor } from "~/feature/ProductDescriptor/v1/types";
import { Button, type ButtonProps } from "~/common/components/Button/Button";
import { canSeeAddToCartButton, canSeeWishlistButton } from "~/utils/features";
import { DefaultButtonIcon } from "~/common/components/icon/Icon";
import { isProductPMA } from "~/feature/ProductDescriptor/v1/productDescriptorUtils";
import { IconType } from "~/common/components/icon/IconType";
import { Trans } from "react-i18next";
import { userIsCustomer, userIsSalesman } from "~/utils/roles";
import { SrpButtons } from "~/Search/components/ProductResults/views/ListView/components/SearchResultButtons";
import { EwellixRequestAQuoteButton } from "~/common/components/Button/PreconfiguredButtons/EwellixRequestAQuoteButton";

type ThemeProps = Pick<ButtonProps, "color" | "variant">;
type ExternalButtonProps = ButtonProps & {
  forceChangeToQuote?: boolean;
};

const EWELLIX_BUTTONS = [
  {
    visible: (product: ProductDescriptor) => {
      return true;
    },
    propsKey: "requestAQuote",
    render: (product: ProductDescriptor, theme: ThemeProps, externalButtonProps: ExternalButtonProps) => (
      <EwellixRequestAQuoteButton key={"requestAQuote"} product={product.base} {...theme} {...externalButtonProps} />
    ),
  },
];
const SCHAEFFLER_BUTTONS = [
  {
    visible: (product: ProductDescriptor) =>
      canSeeAddToCartButton(true) && (userIsCustomer() || !productIsLinear(product)),
    propsKey: "atc",
    render: renderATCButton,
  },
  {
    visible: (product: ProductDescriptor) => canSeeWishlistButton(),
    propsKey: "wishlist",
    render: renderWishlistButton,
  },
];

type PropsForButtons = {
  atc?: ExternalButtonProps;
  wishlist?: ExternalButtonProps & { sizeVariant?: "default" | "side" };
  configurator?: ExternalButtonProps;
};

function getButtonTheme(index: number): ThemeProps {
  if (index === 0) {
    return { color: "primary" };
  } else if (index === 1) {
    return { color: "secondary" };
  } else {
    return { variant: "text" };
  }
}

export function renderButtons(product: ProductDescriptor, externalProps?: PropsForButtons) {
  const buttons = product.base.externalBrand ? EWELLIX_BUTTONS : SCHAEFFLER_BUTTONS;
  return buttons
    .filter(({ visible }) => visible(product))
    .map(({ render, propsKey }, index) => {
      const props: ExternalButtonProps = externalProps?.[propsKey ?? ""] ?? {};
      return render(product, getButtonTheme(index), props);
    });
}

function renderATCButton(
  product: ProductDescriptor,
  theme: ThemeProps,
  { forceChangeToQuote, ...externalButtonProps }: ExternalButtonProps,
) {
  let cartButtonLabel;
  let cartButtonIcon;
  let testId;
  if (userIsSalesman()) {
    testId = "pdp-add-to-quote";
    cartButtonLabel = "basket.addToQuote";
    cartButtonIcon = IconType.FileList;
  } else if ((userIsCustomer() && isProductPMA(product)) || forceChangeToQuote) {
    testId = "pdp-add-to-cart";
    cartButtonLabel = "cartPage.btn.quote";
    cartButtonIcon = IconType.FileList;
  } else {
    testId = "pdp-add-to-cart";
    cartButtonLabel = "addToCartPopup.add.submitBtn";
    cartButtonIcon = IconType.Cart;
  }

  return (
    <Button
      key={"buy"}
      startIcon={<DefaultButtonIcon type={cartButtonIcon} />}
      fullWidth={true}
      data-test-id={testId}
      {...theme}
      {...externalButtonProps}
    >
      <Trans i18nKey={cartButtonLabel} />
    </Button>
  );
}
function renderWishlistButton(
  product: ProductDescriptor,
  theme: ThemeProps,
  { sizeVariant, ...externalButtonProps }: ExternalButtonProps & { sizeVariant?: "default" | "side" },
) {
  if (sizeVariant === "side") {
    return SrpButtons.AddToWishlistSmall({ onClick: externalButtonProps.onClick });
  }

  return (
    <Button
      key={"wishlist"}
      startIcon={<DefaultButtonIcon type={IconType.Bookmark} />}
      fullWidth={true}
      data-test-id={"btn-add-to-wishlist"}
      {...theme}
      {...externalButtonProps}
    >
      <Trans i18nKey={"basket.addToWishlist"} />
    </Button>
  );
}

function productIsLinear(product: ProductDescriptor) {
  return !!product.base.configuratorUrl;
}
